import {TJson, unwrap} from '@packages/systems/plugins/Json';
import {TBoolean} from '@packages/systems/plugins-utils/Boolean';
import {prependRef, foldRight} from '@packages/systems/plugins/List';
import {LOGIC_BLOCK_CATEGORIES} from '@packages/systems/flows/shared';
import {EmailNotification} from './icons';
import {
  type Type,
  Result,
  withSystem,
  Plugins,
  Expressions,
  withMeta,
  TypeImplementation,
} from '@packages/systems/wfdl';
import {
  functionOf,
  TRecord,
  TEmptyRecord,
  TUnion,
  TList,
  TNumber,
  TText,
  TTypeReference,
  TTypeApplication,
  TTypeConstructor,
  ERecord,
  ECall,
  EHole,
  ECase,
  EUnion,
  EList,
  EFunction,
  EText,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {
  type Qualified,
  type TypeName,
  qualify,
  identifier,
  pluginName,
  typeName,
  iconName,
} from '@packages/systems/wfdl-Qualified';
import {
  func,
  call1,
  call2,
  $,
  _$,
  tyfun,
  forall,
  a,
  b,
  c,
} from '@packages/utilities/wfdl-helpers';
import {Pure, Bind, Apply, Fail} from './EffectType';
import {
  sendEmail,
  httpRequest,
  cmsCreateItem,
  consoleLog,
  cmsDeleteItem,
  cmsLookupItem,
  cmsUpdateItem,
  deleteUser,
  updateUser,
} from './Capabilities';
import BindingIcon from './icons/binding';
import {SendNotification} from './SendNotification';
import {TDateTime} from '@packages/systems/plugins/DateTime';

const error = TTypeReference('error');
const otherErrors = TTypeReference('other errors');

const effectPluginName = pluginName('Effect');
const name = <nameType>(n: nameType): Qualified<nameType> =>
  qualify(effectPluginName, n);

export const effectTypeName: Qualified<TypeName> = name(typeName('Effect'));

const tyEffect = TTypeConstructor(effectTypeName);

export const TEffect = (err: Type, ret: Type): Type =>
  TTypeApplication({
    con: TTypeApplication({con: tyEffect, arg: err}),
    arg: ret,
  });

export const pure = call1(_$('Effect', 'pure'));
export const bind = call2(_$('Effect', 'bind'));
export const apply = call2(_$('Effect', 'apply'));
export const join = call1(_$('Effect', 'join'));
export const fail = call1(_$('Effect', 'fail'));
export const lookup = call2(_$('Effect', 'Lookup'));
const log = call1(_$('Effect', 'Log to console'));

export function flow(blocksArgs: Array<[string, Expression]>): Expression {
  if (blocksArgs.length === 0) return EHole();

  const [, lastBlockExpr] = blocksArgs[blocksArgs.length - 1]!;

  return blocksArgs
    .slice(0, -1)
    .reduceRight(
      (body, [param, expr]) => bind(expr, EFunction({param, body})),
      lastBlockExpr
    );
}

const parallel = (effs: Array<Expression>, fn: Expression) =>
  join(effs.reduce(apply, pure(fn)));

export const THttpResponse = TRecord({
  'Response status code': TNumber,
  'Response status message': TText,
  'Response body': TText,
});

const parserFromJsonEnvType = (retType: Type) => ({
  dataType: forall(
    ['other errors'],
    functionOf(
      TJson,
      TEffect(TUnion({'Failed parsing from Json': TJson}, otherErrors), retType)
    )
  ),
  exported: 'hidden',
});

const parserFromJsonExpression = (tag: string) => {
  const cases = {
    Null: failParsingFromJson,
    Boolean: failParsingFromJson,
    Number: failParsingFromJson,
    String: failParsingFromJson,
    Array: failParsingFromJson,
    Object: failParsingFromJson,
  } as const;
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly Null: ExpressionF<any>; readonly Boolean: ExpressionF<any>; readonly Number: ExpressionF<any>; readonly String: ExpressionF<any>; readonly Array: ExpressionF<...>; readonly Object: ExpressionF<...>; }'.
  cases[tag] = _$('Effect', 'pure');

  return EFunction({
    param: 'json',
    body: ECase({
      value: unwrap($('json')),
      cases,
    }),
  });
};

const failParsingFromJson = EFunction({
  param: '_',
  body: fail(EUnion({tag: 'Failed parsing from Json', value: $('json')})),
});

const TJsonList = TList(
  TRecord({
    name: TText,
    value: TJson,
  })
);

const defaultCMSOutput = {
  'Custom Fields': TJsonList,
  'Item ID': TText,
  Name: TText,
  Slug: TText,
  'Item URL': TText,
  'Created On': TText,
  'Created By': TText,
} as const;

export const plugin = Plugins.PrivilegedPlugin({
  icons: {
    Binding: BindingIcon,
    EmailNotificationMedium: EmailNotification,
    EmailNotificationSmall: EmailNotification,
    EmailNotificationBlock: EmailNotification,
  },
  valuePresets: {
    sendEmailNotificationFactory: {
      factory: ECall({
        fun: Expressions.qualifiedVariable('Effect', 'Send email notification'),
        arg: ERecord({
          To: EHole(),
          Subject: EText(''),
          Message: EText(''),
        }),
      }),
      label: 'Send email notification',
      icon: {
        medium: name(iconName('EmailNotificationMedium')),
        small: name(iconName('EmailNotificationSmall')),
      },
      category: LOGIC_BLOCK_CATEGORIES.Actions,
    },
    'Define a value': {
      factory: ECall({
        fun: Expressions.qualifiedVariable('Effect', 'Define a value'),
        arg: EHole(),
      }),
      label: 'Define a value',
      icon: {
        medium: name(iconName('EmailNotificationMedium')),
        small: name(iconName('EmailNotificationSmall')),
      },
      category: LOGIC_BLOCK_CATEGORIES.Utilities,
    },
  },
  dataTypes: {
    Effect: TypeImplementation.Custom({
      params: ['Error', 'Return'],
      parseValue: (_) =>
        Result.Error('Cannot use Literal expressions to create Effect values.'),
      render: () => null,
      icon: TypeImplementation.IconByName(name(iconName('Binding'))),
    }),
    SendNotification,
  },
  values: {
    pure: {
      envType: {
        dataType: forall(['error', 'a'], functionOf(a, TEffect(error, a))),
        exported: 'hidden',
      },
      value: Pure,
    },
    fail: {
      envType: {
        dataType: forall(['error', 'a'], functionOf(error, TEffect(error, a))),
        exported: 'hidden',
      },
      value: Fail,
    },
    bind: {
      envType: {
        dataType: forall(
          ['error', 'a', 'b'],
          tyfun(
            [TEffect(error, a), tyfun([a], TEffect(error, b))],
            TEffect(error, b)
          )
        ),
        exported: 'hidden',
      },
      value: Bind,
    },
    apply: {
      envType: {
        dataType: forall(
          ['error', 'a', 'b'],
          tyfun(
            [TEffect(error, tyfun([a], b)), TEffect(error, a)],
            TEffect(error, b)
          )
        ),
        exported: 'hidden',
      },
      value: Apply,
    },

    join: {
      envType: {
        dataType: forall(
          ['error', 'a'],
          functionOf(TEffect(error, TEffect(error, a)), TEffect(error, a))
        ),
        exported: 'hidden',
      },
      expression: func(
        [identifier('effect')],
        bind($('effect'), func([identifier('x')], $('x')))
      ),
    },
    'Define a value': {
      envType: {
        dataType: forall(['error', 'a'], functionOf(a, TEffect(error, a))),
        exported: 'hidden',
      },
      expression: _$('Effect', 'pure'),
    },

    // f => g => a => bind(f(a))(g)
    compose: {
      envType: {
        dataType: forall(
          ['error', 'a', 'b', 'c'],
          tyfun(
            [tyfun([a], TEffect(error, b)), tyfun([b], TEffect(error, c)), a],
            TEffect(error, c)
          )
        ),
        exported: 'hidden',
      },
      expression: func(
        [identifier('f'), identifier('g'), identifier('a')],
        bind(Expressions._call($('f'), [$('a')]), $('g'))
      ),
    },

    'Parse Boolean from Json': {
      // @ts-expect-error - TS2322 - Type '{ dataType: Type; exported: string; }' is not assignable to type 'EnvType'.
      envType: parserFromJsonEnvType(TBoolean),
      expression: parserFromJsonExpression('Boolean'),
    },

    'Parse Number from Json': {
      // @ts-expect-error - TS2322 - Type '{ dataType: Type; exported: string; }' is not assignable to type 'EnvType'.
      envType: parserFromJsonEnvType(TNumber),
      expression: parserFromJsonExpression('Number'),
    },

    'Parse Text from Json': {
      // @ts-expect-error - TS2322 - Type '{ dataType: Type; exported: string; }' is not assignable to type 'EnvType'.
      envType: parserFromJsonEnvType(TText),
      expression: parserFromJsonExpression('String'),
    },

    'Parse List from Json': {
      // @ts-expect-error - TS2322 - Type '{ dataType: Type; exported: string; }' is not assignable to type 'EnvType'.
      envType: parserFromJsonEnvType(TList(TJson)),
      expression: parserFromJsonExpression('Array'),
    },

    'Parse Name & Value pairs from Json': {
      // @ts-expect-error - TS2322 - Type '{ dataType: Type; exported: string; }' is not assignable to type 'EnvType'.
      envType: parserFromJsonEnvType(
        TList(TRecord({Name: TText, Value: TJson}))
      ),
      expression: parserFromJsonExpression('Object'),
    },

    'Send email notification': {
      envType: {
        dataType: forall(
          ['error'],
          functionOf(
            TRecord({
              To: withSystem(
                {labelVariant: 'hidden', isBindable: false},
                TTypeConstructor(name(typeName('SendNotification')))
              ),
              Subject: withMeta({placeholder: 'Enter email subject...'}, TText),
              Message: withMeta(
                {multiLine: true, placeholder: 'Enter email message...'},
                TText
              ),
            }),
            TEffect(error, TEmptyRecord)
          )
        ),
        exported: 'hidden',
      },
      value: sendEmail,
      icon: name(iconName('EmailNotificationBlock')),
    },
    'HTTP request': {
      envType: {
        dataType: forall(
          ['error'],
          functionOf(
            TRecord({
              url: TText,
              method: TUnion({
                GET: TEmptyRecord,
                POST: TText,
                PUT: TText,
                PATCH: TText,
                DELETE: TEmptyRecord,
              }),
              headers: TList(
                TRecord({
                  name: TText,
                  value: TText,
                })
              ),
            }),
            TEffect(error, THttpResponse)
          )
        ),
        exported: 'hidden',
      },
      value: httpRequest,
    },
    'Low-level lookup CMS item': {
      envType: {
        dataType: forall(
          ['error'],
          functionOf(
            TRecord({
              collectionName: TText,
              searchBy: TRecord({name: TText, value: TJson}),
            }),
            TEffect(
              error,
              TRecord({
                ...defaultCMSOutput,
                'Item Status': TUnion({
                  draft: TEmptyRecord,
                  publish: TEmptyRecord,
                  staged: TEmptyRecord,
                }),
              })
            )
          )
        ),
        exported: 'hidden',
      },
      value: cmsLookupItem,
    },

    'Low-level create CMS item': {
      envType: {
        dataType: forall(
          ['error'],
          functionOf(
            TRecord({
              collectionName: TText,
              itemName: TText,
              itemSlug: TText,
              fields: TJsonList,
              status: TText,
            }),
            TEffect(
              error,
              TRecord({
                ...defaultCMSOutput,
                'Item Status': TUnion({
                  draft: TEmptyRecord,
                  publish: TEmptyRecord,
                  staged: TEmptyRecord,
                }),
              })
            )
          )
        ),
        exported: 'hidden',
      },
      value: cmsCreateItem,
    },

    'Low-level delete User': {
      envType: {
        dataType: forall(
          ['error'],
          functionOf(
            TRecord({
              userIdentifier: TText,
              userIdentifierType: TText,
            }),
            TEffect(
              error,
              TRecord({
                _id: TText,
                createdOn: TDateTime,
                updatedOn: TDateTime,
                emailVerified: TBoolean,
                data: TRecord({
                  name: TText,
                  email: TText,
                  'accept-communications': TBoolean,
                  'accept-privacy': TBoolean,
                }),
                status: TText,
                accessGroups: TList(
                  TRecord({
                    slug: TText,
                    type: TText,
                  })
                ),
              })
            )
          )
        ),
        exported: 'hidden',
      },
      value: deleteUser,
    },

    'Low-level delete CMS item': {
      envType: {
        dataType: forall(
          ['error'],
          functionOf(
            TRecord({
              collectionName: TText,
              itemId: TText,
            }),
            TEffect(error, TRecord({}))
          )
        ),
        exported: 'hidden',
      },
      value: cmsDeleteItem,
    },

    'Low-level update CMS item': {
      envType: {
        dataType: forall(
          ['error'],
          functionOf(
            TRecord({
              collectionName: TText,
              itemId: TText,
              status: TText,
              fields: TList(TRecord({slug: TText, value: TText})),
            }),
            TEffect(
              error,
              TRecord({
                ...defaultCMSOutput,
                'Item Status': TUnion({
                  current: TEmptyRecord,
                  draft: TEmptyRecord,
                  publish: TEmptyRecord,
                  staged: TEmptyRecord,
                  archived: TEmptyRecord,
                }),
              })
            )
          )
        ),
        exported: 'hidden',
      },
      value: cmsUpdateItem,
    },

    'Low-level update User': {
      envType: {
        dataType: forall(
          ['error'],
          functionOf(
            TRecord({
              user: TRecord({
                userIdentifier: TText,
                userIdentifierType: TText,
              }),
              fieldsForUpdate: TJson,
            }),
            TEffect(
              error,
              TRecord({
                _id: TText,
                createdOn: TDateTime,
                updatedOn: TDateTime,
                emailVerified: TBoolean,
                data: TRecord({
                  name: TText,
                  email: TText,
                  'accept-communications': TBoolean,
                  'accept-privacy': TBoolean,
                }),
                status: TText,
                accessGroups: TList(
                  TRecord({
                    slug: TText,
                    type: TText,
                  })
                ),
              })
            )
          )
        ),
        exported: 'hidden',
      },
      value: updateUser,
    },

    Done: {
      envType: {
        dataType: forall(['error'], TEffect(error, TEmptyRecord)),
        exported: 'hidden',
      },
      expression: pure(ERecord({})),
    },
    'Traverse List': {
      envType: {
        dataType: forall(
          ['error', 'a', 'b'],
          tyfun(
            [tyfun([a], TEffect(error, b)), TList(a)],
            TEffect(error, TList(b))
          )
        ),
        exported: 'hidden',
      },
      expression: EFunction({
        param: 'f',
        body: EFunction({
          param: 'items',
          body: foldRight(
            EFunction({
              param: 'item',
              body: EFunction({
                param: 'eff',
                body: apply(
                  apply(pure(prependRef), ECall({fun: $('f'), arg: $('item')})),
                  $('eff')
                ),
              }),
            }),
            pure(EList([])),
            $('items')
          ),
        }),
      }),
    },
    Lookup: {
      envType: {
        dataType: forall(
          ['other errors', 'value'],
          tyfun(
            [
              TText,
              TList(TRecord({name: TText, value: TTypeReference('value')})),
            ],
            TEffect(
              TUnion({'Missing field': TText}, TTypeReference('other errors')),
              TTypeReference('value')
            )
          )
        ),
        exported: 'hidden',
      },
      // @ts-expect-error - TS7006 - Parameter 'key' implicitly has an 'any' type.
      value: (key) => (pairs: any) => {
        for (const pair of pairs) {
          if (pair.name === key) return Pure(pair.value);
        }
        return Fail({
          tag: 'Lookup Fail',
          val: key,
        });
      },
    },
    'Log to console': {
      envType: {
        dataType: forall(
          ['error'],
          functionOf(
            withSystem({label: 'Message', isBindable: true}, TText),
            TEffect(error, TEmptyRecord)
          )
        ),
        exported: 'hidden',
      },
      value: consoleLog,
    },
    'Log in parallel': {
      envType: {
        dataType: forall(
          ['error'],
          tyfun(
            [
              withSystem({label: 'Msg 1', isBindable: true}, TText),
              withSystem({label: 'Msg 2', isBindable: true}, TText),
            ],
            TEffect(
              error,
              TRecord({
                x: TEmptyRecord,
                y: TEmptyRecord,
              })
            )
          )
        ),
        exported: 'hidden',
      },
      expression: func(
        [identifier('msg1'), identifier('msg2')],
        parallel(
          [log($('msg1')), log($('msg2'))],
          func(
            [identifier('x'), identifier('y')],
            pure(
              ERecord({
                x: $('x'),
                y: $('y'),
              })
            )
          )
        )
      ),
    },
  },
});
